import React, { useState, useEffect } from 'react';
import { Drawer, Input, Slider, Space, InputNumber, Form } from 'udesk-ui';
import './index.less';
import UdeskLocales from 'UdeskLocales';

interface EvaluateDrawerProps {
    visible: boolean;
    onClose: () => void;
    courseName?: string;
    onSubmit?: (values: any) => void;
    isView?: boolean;
    initialValues?: {
        courseQuality?: number;
        learningEffect?: number;
        interactionExperience?: number;
        feedback?: string;
    };
}

interface SliderWithNumberProps {
    value?: number;
    onChange?: (value: number) => void;
    desc: string;
    disabled?: boolean;
}

const SliderWithNumber: React.FC<SliderWithNumberProps> = ({ value, onChange, desc, disabled }) => (
    <div className="slider-container">
        <span className="slider-min">0</span>
        <Slider
            style={{ width: '60%', flex: 'none' }}
            value={value}
            onChange={onChange}
            disabled={disabled}
        />
        <span className="slider-max">100</span>
        <InputNumber min={0} max={100} value={value} onChange={onChange} disabled={disabled} />
        <div className="slider-desc">{desc}</div>
    </div>
);

const EvaluateDrawer: React.FC<EvaluateDrawerProps> = ({
    visible,
    onClose,
    courseName,
    onSubmit,
    isView = false,
    initialValues,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    const handleSubmit = () => {
        setLoading(true);
        form.validateFields()
            .then((values) => {
                const processedValues = {
                    ...values,
                    feedback: values.feedback?.trim() || undefined,
                };
                return onSubmit?.(processedValues);
            })
            .then(() => {
                handleClose();
            })
            .catch((error) => {
                console.error('Submit failed:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (visible) {
            form.setFieldsValue(initialValues);
        }
    }, [form, initialValues, visible]);

    return (
        <Drawer
            title={
                <div className="evaluate-drawer-title">
                    <div className="course-name">{courseName}</div>
                </div>
            }
            className="evaluate-drawer"
            width={800}
            visible={visible}
            onClose={handleClose}
            footer={null}
        >
            <Form
                form={form}
                initialValues={{
                    courseQuality: 50,
                    learningEffect: 50,
                    interactionExperience: 50,
                    feedback: '',
                    ...initialValues,
                }}
                layout="horizontal"
                className="evaluate-drawer-content"
            >
                <Form.Item label={/* 课程质量 */UdeskLocales['current'].components.evaluateDrawer.index.courseQuality} name="courseQuality">
                    <SliderWithNumber desc={/* 数值越高表示课程质量越好 */UdeskLocales['current'].components.evaluateDrawer.index.higherValuesIndicateBetterCourseQuality} disabled={isView} />
                </Form.Item>

                <Form.Item label={/* 学习效果 */UdeskLocales['current'].components.evaluateDrawer.index.learningEffect} name="learningEffect">
                    <SliderWithNumber desc={/* 数值越高表示学习效果越好 */UdeskLocales['current'].components.evaluateDrawer.index.theHigherTheValueTheBetterTheLearningEffect} disabled={isView} />
                </Form.Item>

                <Form.Item label={/* 互动体验 */UdeskLocales['current'].components.evaluateDrawer.index.interactiveExperience} name="interactionExperience">
                    <SliderWithNumber desc={/* 数值越高表示课程互动体验越好 */UdeskLocales['current'].components.evaluateDrawer.index.theHigherTheValueTheBetterTheInteractiveExperienceOfTheCourse} disabled={isView} />
                </Form.Item>

                <Form.Item label={/* 建议反馈 */UdeskLocales['current'].components.evaluateDrawer.index.suggestedFeedback} name="feedback">
                    <Input.TextArea
                        placeholder={isView ? undefined : /* 请输入对该课程的评价建议 */UdeskLocales['current'].components.evaluateDrawer.index.pleaseEnterAnEvaluationProposalForThisCourse}
                        rows={4}
                        disabled={isView}
                    />
                </Form.Item>
                {!isView && (
                    <div className="drawer-footer">
                        <Space>
                            <button
                                className="udesk-qa-ui-btn"
                                onClick={handleClose}
                                disabled={loading}
                            >{/* 取消 */}{UdeskLocales['current'].components.evaluateDrawer.index.cancel}</button>
                            <button
                                className="udesk-qa-ui-btn udesk-qa-ui-btn-primary"
                                onClick={handleSubmit}
                                disabled={loading || isView}
                            >{/* 提交 */}{UdeskLocales['current'].components.evaluateDrawer.index.submission}</button>
                        </Space>
                    </div>
                )}
            </Form>
        </Drawer>
    );
};

export default EvaluateDrawer;
